import { useRuntimeConfig, useRouter } from 'nuxt/app';
import { useToast } from '~/composables/useToast'; // ✅ useToast 사용
import { useI18n } from 'vue-i18n';
import { useHandleCode } from './useHandleCode';
import { useAuth } from './useAuth'; // ✅ useAuth 사용

interface ErrorResponse {
	result?: string;
	message?: string;
}

interface CustomOptions {
	responseType?: 'blob' | 'json';
	returnRawResponse?: boolean; // 🔹 Response 객체 자체를 원할 경우 true
}

export function useFetchWithJwt<T = unknown>() {
	const { showToast } = useToast(); // ✅ useToast 상태 사용
	const router = useRouter();
	const config = useRuntimeConfig();
	const { t } = useI18n();
	const { handleErrorCode } = useHandleCode();

	const { accessToken,  logout } = useAuth();

	const fetchWithJwt = async (
		url: string,
		options: RequestInit = {},
		customOptions: CustomOptions = {}
	): Promise<T | Response | null> => {
		const headers: HeadersInit = {
			...options.headers,
			'Authorization': accessToken.value ? `Bearer ${accessToken.value}` : '',
			'Content-Type': 'application/json',
		};

		try {
			const response = await fetch(`${config.public.apiBaseUrl}${url}`, {
				...options,
				headers,
			});

			// ✅ 인증 실패 처리
			if (response.status === 401) {
				await handleUnauthorizedError();
				return null;
			}

			// ✅ 실패 상태 처리 (blob 포함)
			if (!response.ok) {
				try {
					const errorData: ErrorResponse = await response.clone().json();
					handleErrorCode(errorData.message || 'E50001');
				} catch {
					handleErrorCode('E50001');
				}
				return null;
			}

			// ✅ raw Response 반환 옵션 처리
			if (customOptions.returnRawResponse) {
				return response;
			}

			// ✅ blob 처리
			if (customOptions.responseType === 'blob') {
				return (await response.blob()) as T;
			}

			// ✅ 기본 JSON 처리
			return (await response.json()) as T;

		} catch (error) {
			console.error('Fetch error:', error);
			showToast(t('error.E99999'), 'error', 3000, true);
			throw error;
		}
	};

	const fetchFileUploadWithJwt = async (
		url: string,
		options: RequestInit = {},
		customOptions: CustomOptions = {}
	): Promise<T | Response | null> => {
		const headers: HeadersInit = {
			...options.headers,
			'Authorization': accessToken.value ? `Bearer ${accessToken.value}` : ''
		};

		try {
			const response = await fetch(`${config.public.apiBaseUrl}${url}`, {
				...options,
				headers,
			});

			// ✅ 인증 실패 처리
			if (response.status === 401) {
				await handleUnauthorizedError();
				return null;
			}

			// ✅ 실패 상태 처리 (blob 포함)
			if (!response.ok) {
				try {
					const errorData: ErrorResponse = await response.clone().json();
					handleErrorCode(errorData.message || 'E50001');
				} catch {
					handleErrorCode('E50001');
				}
				return null;
			}

			// ✅ raw Response 반환 옵션 처리
			if (customOptions.returnRawResponse) {
				return response;
			}

			// ✅ blob 처리
			if (customOptions.responseType === 'blob') {
				return (await response.blob()) as T;
			}

			// ✅ 기본 JSON 처리
			return (await response.json()) as T;

		} catch (error) {
			console.error('Fetch error:', error);
			showToast(t('error.E99999'), 'error', 3000, true);
			throw error;
		}
	};

	const handleUnauthorizedError = async () => {
		await logout(); // ✅ useAuth의 logout() 사용
		router.push("/");
		showToast(t(`errorCodes.E99998`), 'error', 3000, true); // ✅ `useToast`의 showToast 사용
	};

	return {
		fetchWithJwt,
		fetchFileUploadWithJwt
	};
}
